import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box, Drawer, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { SideNavSection } from './side-nav-section';

const SIDE_NAV_WIDTH = 280;

const getNavDensityVars = (theme) => ({
  '--nav-item-root-py': '5px',
  '--nav-item-sub-py': '3px',
  '--nav-item-root-min-height': '38px',
  '--nav-item-sub-min-height': '30px',
  '--nav-item-root-line-height': '20px',
  '--nav-item-sub-line-height': '18px',
  '--nav-item-root-font-size': '13px',
  '--nav-item-sub-font-size': '12px',
  '--nav-item-root-font-weight': '600',
  '--nav-item-sub-font-weight': '500',
  '--nav-item-gap': '10px',
  '--nav-item-px': '10px',
  '--nav-list-gap': '2px',
  '--nav-sub-list-gap': '2px',
  '--nav-collapse-gap': '6px',
  '--nav-item-root-icon-size': '20px',
  '--nav-item-root-icon-box': '20px',
  '--nav-tree-indent': '14px',
  '--nav-tree-elbow-size': '8px',
  '--nav-sub-tree-color': theme.palette.primary.main,
  '--nav-sub-tree-active-color': theme.palette.primary.main,
  '--nav-branch-open-bg': alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.06 : 0.06),
  '--nav-sub-item-active-bg': alpha(theme.palette.primary.main, 0.08),
});

const useCssVars = (color) => {
  const theme = useTheme();

  return useMemo(() => {
    const densityVars = getNavDensityVars(theme);

    switch (color) {
      case 'blend-in':
        if (theme.palette.mode === 'dark') {
          return {
            ...densityVars,
            '--nav-bg': theme.palette.background.default,
            '--nav-color': theme.palette.neutral[100],
            '--nav-border-color': theme.palette.neutral[700],
            '--nav-logo-border': theme.palette.neutral[700],
            '--nav-section-title-color': theme.palette.neutral[400],
            '--nav-item-color': theme.palette.neutral[400],
            '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
            '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
            '--nav-item-active-color': theme.palette.text.primary,
            '--nav-item-disabled-color': theme.palette.neutral[600],
            '--nav-item-icon-color': theme.palette.neutral[500],
            '--nav-item-icon-active-color': theme.palette.primary.main,
            '--nav-item-icon-disabled-color': theme.palette.neutral[700],
            '--nav-item-chevron-color': theme.palette.neutral[700],
            '--nav-scrollbar-color': theme.palette.neutral[400]
          };
        }
        return {
          ...densityVars,
          '--nav-bg': theme.palette.background.default,
          '--nav-color': theme.palette.text.primary,
          '--nav-border-color': theme.palette.neutral[100],
          '--nav-logo-border': theme.palette.neutral[100],
          '--nav-section-title-color': theme.palette.neutral[400],
          '--nav-item-color': theme.palette.text.secondary,
          '--nav-item-hover-bg': theme.palette.action.hover,
          '--nav-item-active-bg': theme.palette.action.selected,
          '--nav-item-active-color': theme.palette.text.primary,
          '--nav-item-disabled-color': theme.palette.neutral[400],
          '--nav-item-icon-color': theme.palette.neutral[400],
          '--nav-item-icon-active-color': theme.palette.primary.main,
          '--nav-item-icon-disabled-color': theme.palette.neutral[400],
          '--nav-item-chevron-color': theme.palette.neutral[400],
          '--nav-scrollbar-color': theme.palette.neutral[900]
        };

      case 'discreet':
        if (theme.palette.mode === 'dark') {
          return {
            ...densityVars,
            '--nav-bg': theme.palette.neutral[900],
            '--nav-color': theme.palette.neutral[100],
            '--nav-border-color': theme.palette.neutral[700],
            '--nav-logo-border': theme.palette.neutral[700],
            '--nav-section-title-color': theme.palette.neutral[400],
            '--nav-item-color': theme.palette.neutral[400],
            '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
            '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
            '--nav-item-active-color': theme.palette.text.primary,
            '--nav-item-disabled-color': theme.palette.neutral[600],
            '--nav-item-icon-color': theme.palette.neutral[500],
            '--nav-item-icon-active-color': theme.palette.primary.main,
            '--nav-item-icon-disabled-color': theme.palette.neutral[700],
            '--nav-item-chevron-color': theme.palette.neutral[700],
            '--nav-scrollbar-color': theme.palette.neutral[400]
          };
        }
        return {
          ...densityVars,
          '--nav-bg': theme.palette.neutral[50],
          '--nav-color': theme.palette.text.primary,
          '--nav-border-color': theme.palette.divider,
          '--nav-logo-border': theme.palette.neutral[200],
          '--nav-section-title-color': theme.palette.neutral[500],
          '--nav-item-color': theme.palette.neutral[500],
          '--nav-item-hover-bg': theme.palette.action.hover,
          '--nav-item-active-bg': theme.palette.action.selected,
          '--nav-item-active-color': theme.palette.text.primary,
          '--nav-item-disabled-color': theme.palette.neutral[400],
          '--nav-item-icon-color': theme.palette.neutral[400],
          '--nav-item-icon-active-color': theme.palette.primary.main,
          '--nav-item-icon-disabled-color': theme.palette.neutral[400],
          '--nav-item-chevron-color': theme.palette.neutral[400],
          '--nav-scrollbar-color': theme.palette.neutral[900]
        };

      case 'evident':
        if (theme.palette.mode === 'dark') {
          return {
            ...densityVars,
            '--nav-bg': theme.palette.neutral[800],
            '--nav-color': theme.palette.common.white,
            '--nav-border-color': 'transparent',
            '--nav-logo-border': theme.palette.neutral[700],
            '--nav-section-title-color': theme.palette.neutral[400],
            '--nav-item-color': theme.palette.neutral[400],
            '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
            '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
            '--nav-item-active-color': theme.palette.common.white,
            '--nav-item-disabled-color': theme.palette.neutral[500],
            '--nav-item-icon-color': theme.palette.neutral[400],
            '--nav-item-icon-active-color': theme.palette.primary.main,
            '--nav-item-icon-disabled-color': theme.palette.neutral[500],
            '--nav-item-chevron-color': theme.palette.neutral[600],
            '--nav-scrollbar-color': theme.palette.neutral[400]
          };
        }
        return {
          ...densityVars,
          '--nav-bg': theme.palette.neutral[800],
          '--nav-color': theme.palette.common.white,
          '--nav-border-color': 'transparent',
          '--nav-logo-border': theme.palette.neutral[700],
          '--nav-section-title-color': theme.palette.neutral[400],
          '--nav-item-color': theme.palette.neutral[400],
          '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
          '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
          '--nav-item-active-color': theme.palette.common.white,
          '--nav-item-disabled-color': theme.palette.neutral[500],
          '--nav-item-icon-color': theme.palette.neutral[400],
          '--nav-item-icon-active-color': theme.palette.primary.main,
          '--nav-item-icon-disabled-color': theme.palette.neutral[500],
          '--nav-item-chevron-color': theme.palette.neutral[600],
          '--nav-scrollbar-color': theme.palette.neutral[400]
        };

      default:
        return densityVars;
    }
  }, [theme, color]);
};

export const SideNav = ({
  color = 'evident',
  sections = [],
  isDesktop = true,
  openMenu = false,
  onMenuClose,
}) => {

  const { pathname } = useLocation();
  const cssVars = useCssVars(color);

  const variant = isDesktop ? 'permanent' : 'temporary';

  const paperSx = {
    ...cssVars,
    backgroundColor: 'var(--nav-bg)',
    borderRightColor: 'var(--nav-border-color)',
    borderRightStyle: 'solid',
    borderRightWidth: 1,
    color: 'var(--nav-color)',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: SIDE_NAV_WIDTH,
  };

  return (
    <Drawer
      anchor="left"
      // ModalProps={variant === 'temporary' ? { keepMounted: true } : undefined}
      onClose={onMenuClose}
      open={openMenu}
      PaperProps={{
        sx: paperSx,
      }}
      variant={'temporary'}
    >
      <Stack
        alignItems="center"
        direction="row"
        spacing={2}
        sx={{ flexShrink: 0, p: 3 }}
      >
        <Box
          component={Link}
          to="/dashboard"
          sx={{
            alignItems: 'center',
            borderColor: 'var(--nav-logo-border)',
            borderRadius: 1,
            borderStyle: 'solid',
            borderWidth: 1,
            color: 'inherit',
            display: 'flex',
            height: 40,
            justifyContent: 'center',
            minWidth: 40,
            p: '4px',
            textDecoration: 'none',
            typography: 'subtitle2'
          }}
        >
          EP
        </Box>
        <Typography
          component={Link}
          to="/dashboard"
          sx={{
            color: 'inherit',
            flexGrow: 1,
            fontWeight: 600,
            textDecoration: 'none'
          }}
          variant="subtitle1"
        >
          Eletropontes
        </Typography>
      </Stack>

      <Box
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          overflow: 'auto',
          px: 1.5,
        }}
      >
        <Stack
          component="nav"
          spacing={0}
        >
          {sections.map((section, index) => (
            <SideNavSection
              items={section.items}
              key={section.subheader || `section-${index}`}
              pathname={pathname}
              subheader={section.subheader}
            />
          ))}
        </Stack>
      </Box>

      {/* <Box sx={{ flexShrink: 0, p: 3 }}>
        <Typography variant="subtitle1">
          Precisa de ajuda?
        </Typography>
        <Typography
          color="neutral.400"
          sx={{ mb: 2 }}
          variant="body2"
        >
          Acesse a área de documentos do sistema.
        </Typography>
        <Button
          color="primary"
          component={Link}
          fullWidth
          startIcon={(
            <SvgIcon fontSize="small">
              <DescriptionOutlined />
            </SvgIcon>
          )}
          to="/documentos"
          variant="contained"
        >
          Documentos
        </Button>
      </Box> */}
    </Drawer>
  );
};

SideNav.propTypes = {
  color: PropTypes.oneOf(['blend-in', 'discreet', 'evident']),
  isDesktop: PropTypes.bool,
  menuOpen: PropTypes.bool,
  onMenuClose: PropTypes.func,
  sections: PropTypes.array,
};
